// src/components/Services/PDF/ServiceOrderServiceDocument.tsx
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';
import { formatCurrency } from '@/utils/formatters';
import { safeFormatDate } from '@/utils/dateHelpers';

// ─── Paleta (hex fixo — nunca var() ou Tailwind) ──────────────────────────────

const COLORS = {
  primary:   '#0f172a',
  secondary: '#475569',
  accent:    '#2563eb',
  border:    '#e2e8f0',
  headerBg:  '#1e3a5f',
  rowAlt:    '#f8fafc',
  white:     '#ffffff',
  total:     '#dcfce7',
  equipBg:   '#f1f5f9',
};

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface ServiceOrderItem {
  description: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  item_type: 'part' | 'service';
}

export interface ServiceOrderEquipmentSection {
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  reported_issue: string | null;
  items: ServiceOrderItem[];
}

export interface ServiceOrderServiceDocumentProps {
  serviceOrder: {
    service_order_number: string;
    created_at: string;
    actual_delivery_date: string | null;
    notes: string | null;
    total_value: number;
    total_parts: number;
    total_services: number;
  };
  customer: {
    name: string;
    document: string | null;
    phone: string | null;
    email: string | null;
  };
  equipment: ServiceOrderEquipmentSection[];
  company: {
    name: string;
    cnpj: string | null;
    address: string | null;
    phone: string | null;
  };
}

// ─── Estilos ──────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    padding: 32,
    color: COLORS.primary,
    backgroundColor: COLORS.white,
  },

  // Cabeçalho
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: COLORS.headerBg,
    padding: 16,
    marginBottom: 16,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  headerCompanyName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 16,
    color: COLORS.white,
    marginBottom: 4,
  },
  headerCompanyInfo: {
    fontSize: 9,
    color: COLORS.white,
    marginBottom: 2,
  },
  headerDocLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
    color: COLORS.white,
    marginBottom: 4,
  },
  headerOsNumber: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 20,
    color: COLORS.white,
    marginBottom: 4,
  },
  headerDate: {
    fontSize: 10,
    color: COLORS.white,
  },

  // Seções
  sectionTitleBar: {
    backgroundColor: COLORS.accent,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginBottom: 6,
  },
  sectionTitleText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS.white,
  },
  section: {
    marginBottom: 12,
  },

  // Grid 2 colunas de dados
  grid2: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  gridCell: {
    flex: 1,
    paddingRight: 8,
  },
  gridLabel: {
    fontSize: 7,
    color: COLORS.secondary,
    marginBottom: 1,
  },
  gridValue: {
    fontSize: 9,
    color: COLORS.primary,
  },

  // Equipamento
  equipHeader: {
    backgroundColor: COLORS.equipBg,
    paddingHorizontal: 8,
    paddingVertical: 5,
    marginBottom: 6,
    marginTop: 8,
  },
  equipHeaderText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS.primary,
  },

  // Tabela de itens
  tableContainer: {
    marginBottom: 8,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: COLORS.primary,
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  tableHeaderCell: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: COLORS.white,
  },
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderBottom: `0.5pt solid ${COLORS.border}`,
  },
  tableRowAlt: {
    backgroundColor: COLORS.rowAlt,
  },
  colDesc: {
    flex: 1,
    fontSize: 8,
    color: COLORS.primary,
  },
  colType: {
    width: 50,
    fontSize: 8,
    color: COLORS.secondary,
    textAlign: 'center',
  },
  colQty: {
    width: 30,
    fontSize: 8,
    textAlign: 'right',
    color: COLORS.secondary,
  },
  colUnit: {
    width: 60,
    fontSize: 8,
    textAlign: 'right',
    color: COLORS.secondary,
  },
  colTotal: {
    width: 64,
    fontSize: 8,
    textAlign: 'right',
    color: COLORS.primary,
  },
  emptyRow: {
    padding: 8,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 8,
    color: COLORS.secondary,
  },

  // Totais
  totalsSeparator: {
    borderTop: `1pt solid ${COLORS.border}`,
    marginTop: 12,
    marginBottom: 8,
  },
  totalsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  totalItem: {
    alignItems: 'flex-end',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 3,
  },
  totalItemGreen: {
    backgroundColor: COLORS.total,
  },
  totalItemLabel: {
    fontSize: 7,
    color: COLORS.secondary,
    marginBottom: 2,
  },
  totalItemValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.primary,
  },
  totalItemValueLarge: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.primary,
  },

  // Observações
  notesSection: {
    marginTop: 12,
    padding: 8,
    border: `0.5pt solid ${COLORS.border}`,
    borderRadius: 3,
  },
  notesSectionTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: COLORS.secondary,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  notesText: {
    fontSize: 9,
    color: COLORS.primary,
    lineHeight: 1.5,
  },

  // Assinaturas
  signatureSection: {
    marginTop: 24,
    paddingTop: 12,
    borderTop: `0.5pt solid ${COLORS.border}`,
  },
  signatureDeclaration: {
    fontSize: 8,
    color: COLORS.secondary,
    marginBottom: 24,
    lineHeight: 1.4,
    textAlign: 'center',
  },
  signatureLine: {
    borderTop: `1pt solid ${COLORS.primary}`,
    paddingTop: 4,
    marginBottom: 6,
    width: '70%',
    alignSelf: 'center',
    textAlign: 'center',
  },
  signatureLabel: {
    fontSize: 8,
    color: COLORS.secondary,
    textAlign: 'center',
  },
  signatureFields: {
    marginTop: 12,
    width: '70%',
    alignSelf: 'center',
  },
  signatureField: {
    fontSize: 8,
    color: COLORS.primary,
    borderBottom: `0.5pt solid ${COLORS.border}`,
    paddingBottom: 6,
    marginBottom: 8,
  },
  dateField: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 16,
  },
  dateFieldText: {
    fontSize: 8,
    color: COLORS.primary,
    borderBottom: `0.5pt solid ${COLORS.border}`,
    paddingBottom: 4,
  },

  // Rodapé fixo
  footer: {
    position: 'absolute',
    bottom: 16,
    left: 32,
    right: 32,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTop: `0.5pt solid ${COLORS.border}`,
    paddingTop: 4,
  },
  footerText: {
    fontSize: 7,
    color: COLORS.secondary,
  },
});

// ─── Componente ───────────────────────────────────────────────────────────────

export function ServiceOrderServiceDocument({
  serviceOrder,
  customer,
  equipment,
  company,
}: ServiceOrderServiceDocumentProps) {
  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* 1. Cabeçalho */}
        <View style={s.header} fixed>
          <View style={s.headerLeft}>
            <Text style={s.headerCompanyName}>{company.name}</Text>
            {company.cnpj ? (
              <Text style={s.headerCompanyInfo}>CNPJ: {company.cnpj}</Text>
            ) : null}
            {company.address ? (
              <Text style={s.headerCompanyInfo}>{company.address}</Text>
            ) : null}
            {company.phone ? (
              <Text style={s.headerCompanyInfo}>Tel: {company.phone}</Text>
            ) : null}
          </View>
          <View style={s.headerRight}>
            <Text style={s.headerDocLabel}>DOCUMENTO DE SERVIÇO</Text>
            <Text style={s.headerOsNumber}>
              OS Nº {serviceOrder.service_order_number}
            </Text>
            <Text style={s.headerDate}>
              Emissão: {safeFormatDate(serviceOrder.created_at)}
            </Text>
          </View>
        </View>

        {/* 2. Dados do Cliente */}
        <View style={s.section}>
          <View style={s.sectionTitleBar}>
            <Text style={s.sectionTitleText}>Dados do Cliente</Text>
          </View>
          <View style={s.grid2}>
            <View style={s.gridCell}>
              <Text style={s.gridLabel}>Nome</Text>
              <Text style={s.gridValue}>{customer.name}</Text>
            </View>
            <View style={s.gridCell}>
              <Text style={s.gridLabel}>CPF / CNPJ</Text>
              <Text style={s.gridValue}>
                {customer.document ? customer.document : '—'}
              </Text>
            </View>
          </View>
          <View style={s.grid2}>
            <View style={s.gridCell}>
              <Text style={s.gridLabel}>Telefone</Text>
              <Text style={s.gridValue}>
                {customer.phone ? customer.phone : '—'}
              </Text>
            </View>
            <View style={s.gridCell}>
              <Text style={s.gridLabel}>E-mail</Text>
              <Text style={s.gridValue}>
                {customer.email ? customer.email : '—'}
              </Text>
            </View>
          </View>
        </View>

        {/* 3. Equipamentos */}
        {equipment.length > 0 ? (
          <View style={s.section}>
            <View style={s.sectionTitleBar}>
              <Text style={s.sectionTitleText}>Serviços Executados</Text>
            </View>
            {equipment.map((eq, idx) => (
              <View key={idx}>
                {/* Subtítulo do equipamento */}
                <View style={s.equipHeader}>
                  <Text style={s.equipHeaderText}>
                    Equipamento {idx + 1}
                    {eq.brand ? ` — ${eq.brand}` : ''}
                    {eq.model ? ` ${eq.model}` : ''}
                  </Text>
                </View>

                {/* Grid de detalhes */}
                <View style={s.grid2}>
                  <View style={s.gridCell}>
                    <Text style={s.gridLabel}>Marca</Text>
                    <Text style={s.gridValue}>
                      {eq.brand ? eq.brand : '—'}
                    </Text>
                  </View>
                  <View style={s.gridCell}>
                    <Text style={s.gridLabel}>Modelo</Text>
                    <Text style={s.gridValue}>
                      {eq.model ? eq.model : '—'}
                    </Text>
                  </View>
                </View>
                <View style={s.grid2}>
                  <View style={s.gridCell}>
                    <Text style={s.gridLabel}>Nº de Série</Text>
                    <Text style={s.gridValue}>
                      {eq.serial_number ? eq.serial_number : '—'}
                    </Text>
                  </View>
                  <View style={s.gridCell}>
                    <Text style={s.gridLabel}>Defeito Relatado</Text>
                    <Text style={s.gridValue}>
                      {eq.reported_issue ? eq.reported_issue : '—'}
                    </Text>
                  </View>
                </View>

                {/* Tabela de itens */}
                <View style={s.tableContainer}>
                  {/* Cabeçalho */}
                  <View style={s.tableHeaderRow} wrap={false}>
                    <Text style={[s.tableHeaderCell, s.colDesc]}>Descrição</Text>
                    <Text style={[s.tableHeaderCell, s.colType]}>Tipo</Text>
                    <Text style={[s.tableHeaderCell, s.colQty]}>Qtd</Text>
                    <Text style={[s.tableHeaderCell, s.colUnit]}>Valor Unit.</Text>
                    <Text style={[s.tableHeaderCell, s.colTotal]}>Total</Text>
                  </View>

                  {/* Linhas */}
                  {eq.items.length > 0 ? (
                    eq.items.map((item, iIdx) => (
                      <View
                        key={iIdx}
                        style={[
                          s.tableRow,
                          iIdx % 2 !== 0 ? s.tableRowAlt : {},
                        ]}
                        wrap={false}
                      >
                        <Text style={s.colDesc}>{item.description}</Text>
                        <Text style={s.colType}>
                          {item.item_type === 'part' ? 'Peça' : 'Serviço'}
                        </Text>
                        <Text style={s.colQty}>{item.quantity}</Text>
                        <Text style={s.colUnit}>
                          {formatCurrency(item.unit_price)}
                        </Text>
                        <Text style={s.colTotal}>
                          {formatCurrency(item.total_price)}
                        </Text>
                      </View>
                    ))
                  ) : (
                    <View style={s.emptyRow} wrap={false}>
                      <Text style={s.emptyText}>Nenhum item registrado</Text>
                    </View>
                  )}
                </View>
              </View>
            ))}
          </View>
        ) : null}

        {/* 4. Totais */}
        <View style={s.totalsSeparator} />
        <View style={s.totalsContainer}>
          {serviceOrder.total_parts > 0 ? (
            <View style={s.totalItem}>
              <Text style={s.totalItemLabel}>Total Peças</Text>
              <Text style={s.totalItemValue}>
                {formatCurrency(serviceOrder.total_parts)}
              </Text>
            </View>
          ) : null}
          {serviceOrder.total_services > 0 ? (
            <View style={s.totalItem}>
              <Text style={s.totalItemLabel}>Total Serviços</Text>
              <Text style={s.totalItemValue}>
                {formatCurrency(serviceOrder.total_services)}
              </Text>
            </View>
          ) : null}
          <View style={[s.totalItem, s.totalItemGreen]}>
            <Text style={s.totalItemLabel}>TOTAL GERAL</Text>
            <Text style={s.totalItemValueLarge}>
              {formatCurrency(serviceOrder.total_value)}
            </Text>
          </View>
        </View>

        {/* 5. Observações ao cliente */}
        {serviceOrder.notes ? (
          <View style={s.notesSection}>
            <Text style={s.notesSectionTitle}>Observações</Text>
            <Text style={s.notesText}>{serviceOrder.notes}</Text>
          </View>
        ) : null}

        {/* 6. Assinatura */}
        <View style={s.signatureSection}>
          <Text style={s.signatureDeclaration}>
            Declaro que recebi o(s) equipamento(s) descrito(s) acima em perfeito estado,{'\n'}
            conforme os serviços executados neste documento.
          </Text>
          <View style={s.dateField}>
            <Text style={s.dateFieldText}>
              {'Data: ______ / ______ / ______'}
            </Text>
          </View>
          <View style={s.signatureLine}>
            <Text style={s.signatureLabel}>Assinatura do Cliente</Text>
          </View>
          <View style={s.signatureFields}>
            <Text style={s.signatureField}>
              Nome completo: _____________________________________________
            </Text>
            <Text style={s.signatureField}>
              CPF / RG: ____________________________________________________
            </Text>
          </View>
        </View>

        {/* 7. Rodapé fixo com número de página */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>GOnx ERP — {company.name}</Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
